import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import MyAppText from '../../../components/MyAppText';
import { licensePlateRecognitionService } from '../../../services/central-api/license-plate-recognition';
import { ScrollView } from 'react-native-web-hover';
import FilterInputNumeric from '../../../components/Filter/components/FilterInputNumeric';
import CamerasDropdown from '../../../components/lpr/CamerasDropdown';
import BrandsDropdown from '../../../components/lpr/BrandsDropdown';
import ModelsDropdown from '../../../components/lpr/ModelDropdown';
import ColorsDropdown from '../../../components/lpr/ColorDropdown';
import TypesDropdown from '../../../components/lpr/TypesDropdown';
import MyRadio from '../../../components/MyRadio';
import DateTimePicker from '../../../components/DateTimePicker';
import UnitNamesDropdown from '../../../components/lpr/UnitNamesDropdown';
import { useAuth } from '../../../context/Auth';
import { useForm } from 'react-hook-form';
import { isPlateInvalid } from '../../../components/helpers/plate';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onClose: () => void;
}

const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

interface ReportForm {
    name: string;
    plate: string;
    brand: string;
    model: string;
    color: string;
    type: string;
    year: number | null;
    startInDateFilter: Date | null;
    startInHourFilter: string;
    endInDateFilter: Date | null;
    endInHourFilter: string;
    selectedCameraFilter: string[];
    selectedUnitNamesIds: number[];
    searchType: 'plate' | 'characteristic';
}

export default function ReportModal({ onClose, isModalVisible, setModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const defaultEmptyValues: ReportForm = {
        name: '',
        plate: '',
        brand: '',
        model: '',
        color: '',
        type: '',
        year: null,
        startInDateFilter: null,
        startInHourFilter: '',
        endInDateFilter: null,
        endInHourFilter: '',
        selectedCameraFilter: [],
        selectedUnitNamesIds: [],
        searchType: 'plate',
    };

    const form = useForm<ReportForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    useEffect(() => {
        if (form.watch('selectedCameraFilter').length > 0) {
            form.clearErrors('selectedUnitNamesIds');
        }
    }, [form.watch('selectedCameraFilter')]);

    useEffect(() => {
        if (form.watch('selectedUnitNamesIds').length > 0) {
            form.clearErrors('selectedCameraFilter');
        }
    }, [form.watch('selectedUnitNamesIds')]);

    function isNameInvalid(value: string) {
        return value == '' || !regex.test(value);
    }

    function onCloseNewReportModal() {
        setModalVisible(false);
        resetReportForm();
    }

    async function formSubmit() {
        try {
            const values = form.getValues();
            await licensePlateRecognitionService.createReport({
                name: values.name,
                startDate: moment(`${moment(values.startInDateFilter).format('DD/MM/YYYY')} ${values.startInHourFilter}`, 'DD/MM/YYYY HH:mm').toISOString(),
                endDate: moment(`${moment(values.endInDateFilter).format('DD/MM/YYYY')} ${values.endInHourFilter}`, 'DD/MM/YYYY HH:mm').toISOString(),
                plate: values.plate,
                brand: values.brand !== '' ? values.brand : undefined,
                model: values.model !== '' ? values.model : undefined,
                color: values.color !== '' ? values.color : undefined,
                type: values.type !== '' ? values.type : undefined,
                year: values.year !== null ? values.year : undefined,
                camerasIds: values.selectedCameraFilter,
                unitNamesIds: values.selectedUnitNamesIds
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            onCloseNewReportModal();
            onClose();
        }
    }

    function resetReportForm() {
        form.reset(defaultEmptyValues);
    }

    return (
        <Modal transparent={true} visible={isModalVisible} animationType='fade' onRequestClose={() => onCloseNewReportModal()} >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: 400 }]}>
                    <ScrollView contentContainerStyle={styles.scrollContent}>
                        <MyAppText style={{ fontSize: 22, fontWeight: 'bold', color: theme.colors.labelColor }}>{translate('detectionsReport')}</MyAppText>
                        <FormInput
                            label={translate('name')}
                            form={form}
                            fieldName='name'
                            required
                            customValidations={{
                                isValid: (val: string) => !isNameInvalid(val) || translate('invalid_field')
                            }}
                            invalid={isNameInvalid}
                            errorMessage={translate('invalidCharacters')}
                        />
                        <MyRadio
                            form={form}
                            fieldName='searchType'
                            options={[
                                { label: translate('plate'), value: 'plate' },
                                { label: translate('characteristic'), value: 'characteristic' },
                            ]}
                        />
                        {form.watch('searchType') == 'plate' ?
                            <FormInput label={translate('plate')}
                                invalid={() => false}
                                form={form}
                                fieldName='plate'
                                toUppercase
                                required
                                customValidations={{
                                    isValid: (val) => !isPlateInvalid(val) || translate('invalid_field')
                                }}
                            /> :
                            <>
                                <TypesDropdown
                                    form={form}
                                    fieldName='type'
                                    zIndex={8}
                                />
                                <BrandsDropdown
                                    form={form}
                                    fieldName='brand'
                                    zIndex={7}
                                />
                                <ModelsDropdown
                                    form={form}
                                    fieldName='model'
                                    zIndex={6}
                                    loadValues={form.watch('brand') !== null && form.watch('brand') !== ''}
                                />
                                <ColorsDropdown
                                    form={form}
                                    fieldName='color'
                                    zIndex={5}
                                    loadValues={form.watch('model') !== null && form.watch('model') !== ''}
                                />
                                <FilterInputNumeric
                                    label={translate('year')}
                                    form={form}
                                    fieldName='year'
                                    placeholder={translate('typeAValue')}
                                    maxValue={moment().year()}
                                    minValue={1900}
                                    disabled={form.watch('color') === null || form.watch('color') === ''}
                                    customValidations={{
                                        isValid: (val) => !(val !== null && (val > moment().year() || val < 1900)) || translate('invalid_field')
                                    }}
                                />
                            </>
                        }
                        <CamerasDropdown
                            showLabel={true}
                            form={form}
                            fieldName='selectedCameraFilter'
                            zIndex={4}
                            required={form.watch('selectedUnitNamesIds').length === 0}
                        />
                        {authenticatedUser?.permissions.lpr_watch_unit_detections &&
                            <UnitNamesDropdown
                                form={form}
                                fieldName='selectedUnitNamesIds'
                                zIndex={3}
                                showLabel={true}
                                required={form.watch('selectedCameraFilter').length === 0}
                            />
                        }

                        <View style={{ zIndex: estimatedStartZIndex }}>
                            <DateTimePicker
                                form={form}
                                dateFieldName='startInDateFilter'
                                timeFieldName='startInHourFilter'
                                linkedFields={['endInDateFilter', 'endInHourFilter']}
                                required
                                customValidations={{
                                    isValidDate: (value, form) => {
                                        const startDate = form.watch('startInDateFilter');
                                        const startHour = form.watch('startInHourFilter');
                                        const endDate = form.watch('endInDateFilter');
                                        const endHour = form.watch('endInHourFilter');

                                        if (!startDate || !startHour || !endDate || !endHour) {
                                            return true;
                                        }

                                        const startDateTime = moment(
                                            `${moment(startDate).format('DD/MM/YYYY')} ${startHour}`,
                                            'DD/MM/YYYY HH:mm'
                                        );
                                        const endDateTime = moment(
                                            `${moment(endDate).format('DD/MM/YYYY')} ${endHour}`,
                                            'DD/MM/YYYY HH:mm'
                                        );

                                        if (!startDateTime.isBefore(endDateTime)) {
                                            return translate('start_date_must_be_before_end_date');
                                        }

                                        return true;
                                    }
                                }}
                                label={translate('beginIn')}
                                maxDate={new Date()}
                                onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                                onCalendarClose={() => setEstimatedStartZIndex(2)}
                                labelFontSize={16}
                            />
                        </View>
                        <View style={{ zIndex: estimatedFinishZIndex }}>
                            <DateTimePicker
                                form={form}
                                dateFieldName='endInDateFilter'
                                timeFieldName='endInHourFilter'
                                linkedFields={['startInDateFilter', 'startInHourFilter']}
                                required
                                label={translate('endIn')}
                                maxDate={new Date()}
                                onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                                onCalendarClose={() => setEstimatedFinishZIndex(1)}
                                labelFontSize={16}
                            />
                        </View>

                    </ScrollView>
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={() => onCloseNewReportModal()}
                        disabled={!form.formState.isValid}
                        confirmText={'download'} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 15,
        maxHeight: 690,
        padding: 20
    },
    scrollContent: {
        rowGap: 15,
        padding: 5,
    }
}));
